import './style.scss'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Card } from 'src/componentv2/Base/Card'
import { Button } from 'src/componentv2/Base/Button'
import { TextField } from 'src/componentv2/Base/TextField'
import { SelectField } from 'src/componentv2/Base/SelectField'
import { RadioCard } from 'src/componentv2/Base/RadioCard'
import { FileUpload, UploadedFile } from 'src/componentv2/Base/FileUpload'
import { PreviewOption } from 'src/componentv2/Base/PreviewOption'
import { ColorField } from 'src/componentv2/Base/ColorField'
import { InfoBanner } from 'src/componentv2/Base/InfoBanner'
import { Modal } from 'src/componentv2/Base/Modal'
import { Notification } from 'src/componentv2/Base/Notification'
import { Icon } from 'src/componentv2/Base/Icon'
import { Box, Flex, Text, tokens } from 'src/componentv2/Base/ui'
import { ThemedVariantSvg } from './ThemedVariantSvg'
import ConfigurationApiContext from 'src/provider/API/v2/call-service/configuration'
import { RiComputerLine, RiSmartphoneLine, RiDeleteBinLine } from 'react-icons/ri'
import variantOneWeb from 'src/assets/v2/img/variant_one_web.svg'
import variantOneMobile from 'src/assets/v2/img/variant_one_mobile.svg'
import variantTwoWeb from 'src/assets/v2/img/variant_two_web.svg'
import variantTwoMobile from 'src/assets/v2/img/variant_two_mobile.svg'
import variantThreeWeb from 'src/assets/v2/img/variant_three_web.svg'
import variantThreeMobile from 'src/assets/v2/img/variant_three_moble.svg'
import plusIcon from 'src/assets/v2/img/plus_icon.svg'
import type {
    ThemeVariantOption, OnboardingSlide, InstitutionBrandingProps, StoredThemeColors,
    CornerRadius, CardStyle, BrandingStepKey,
} from 'src/model/v2/interface/props'
import type { SelectThemeVariant, ThemeConfigurationRequest, OnboardingSlideRequest } from 'src/model/v2/interface/params'
import {
    fontRegular, fontMedium,
    VARIANT_API_MAP, VARIANT_FROM_API,
    CORNER_RADIUS_API_MAP, CORNER_RADIUS_FROM_API,
    TYPOGRAPHY_API_MAP, TYPOGRAPHY_FROM_API,
    CARD_STYLE_API_MAP, CARD_STYLE_FROM_API,
    CORNER_OPTIONS, CARD_STYLE_OPTIONS, TYPOGRAPHY_OPTIONS,
    BRANDING_STEPS, BRANDING_STEP_KEYS, MIN_SLIDES, MAX_SLIDES,
    THEME_COLORS_STORAGE_KEY,
} from 'src/model/v2/interface/constants'
import { loadStoredColors } from 'src/app/dashboardv2/function'

const VARIANTS: ThemeVariantOption[] = [
    { id: 'one', web: variantOneWeb, mobile: variantOneMobile },
    { id: 'two', web: variantTwoWeb, mobile: variantTwoMobile },
    { id: 'three', web: variantThreeWeb, mobile: variantThreeMobile },
]

interface LivePreviewProps {
    variant: ThemeVariantOption
    primaryColor: string
    secondaryColor: string
    darkSecondaryColor: string
    slideMobileImageUrl?: string
    slideWebImageUrl?: string
}

const LivePreview: React.FC<LivePreviewProps> = ({
                                                     variant, primaryColor, secondaryColor, darkSecondaryColor,
                                                     slideMobileImageUrl, slideWebImageUrl,
                                                 }) => {
    const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop')
    const isDesktopView = device === 'desktop'
    const phone = (
        <div className={`branding-phone-svg ${isDesktopView ? 'branding-phone-svg--web' : ''}`}>
            <ThemedVariantSvg
                src={isDesktopView ? variant.web : variant.mobile}
                primaryColor={primaryColor}
                secondaryColor={secondaryColor}
                darkSecondaryColor={darkSecondaryColor}
                imageOverrideUrl={isDesktopView ? slideWebImageUrl : slideMobileImageUrl}
            />
        </div>
    )
    return (
        <Box className="live-preview">
            <Flex justify="space-between" items="center">
                <Text fontSize="1.5" style={{ ...fontMedium, color: '#344054' }}>
                    Live Preview
                </Text>
                <Flex className="live-preview__toggle" gap="0.4">
                    <button
                        type="button"
                        className={`live-preview__toggle-btn ${device === 'desktop' ? 'is-active' : ''}`}
                        onClick={() => setDevice('desktop')}
                        aria-label="Preview desktop"
                    >
                        <RiComputerLine size={18} color={device === 'desktop' ? '#0D968F' : '#98A2B3'} />
                    </button>
                    <button
                        type="button"
                        className={`live-preview__toggle-btn ${device === 'mobile' ? 'is-active' : ''}`}
                        onClick={() => setDevice('mobile')}
                        aria-label="Preview mobile"
                    >
                        <RiSmartphoneLine size={18} color={device === 'mobile' ? '#0D968F' : '#98A2B3'} />
                    </button>
                </Flex>
            </Flex>
            <Flex
                topMargin="1.2"
                gap="1.6"
                items="stretch"
                className="live-preview__stage"
                justify="center"
                borderColor={tokens.border}
                radius="1.2"
                padding="2.4"
            >
                {phone}
            </Flex>
        </Box>
    )
}

interface NewSlideForm {
    title: string
    description: string
    mobileImage: UploadedFile | null
    webImage: UploadedFile | null
    mobileImageUrl?: string
    webImageUrl?: string
    mobileFile?: File
    webFile?: File
}
const EMPTY_SLIDE_FORM: NewSlideForm = {
    title: '', description: '', mobileImage: null, webImage: null,
}

export const InstitutionBranding: React.FC<InstitutionBrandingProps> = ({
                                                                            onComplete, onStepComplete, initial, initialStep, className = '', ...rest
                                                                        }) => {
    const { selectThemeVariant, updateThemeConfiguration, updateOnboardingSlides, getOnboardingSlides, deleteOnboardingSlide } = useContext(ConfigurationApiContext)
    const [step, setStep] = useState(initialStep ?? 0)
    const [variant, setVariant] = useState(
        initial?.variant ? VARIANT_FROM_API[initial.variant] : 'one'
    )
    const [primaryColor, setPrimaryColor] = useState(
        initial?.primaryColor ?? loadStoredColors()?.primaryColor ?? tokens.primary
    )
    const [secondaryColor, setSecondaryColor] = useState(
        initial?.secondaryColor ?? loadStoredColors()?.secondaryColor ?? tokens.primarySoft
    )
    const [darkSecondaryColor, setDarkSecondaryColor] = useState(
        initial?.darkSecondaryColor ?? loadStoredColors()?.darkSecondaryColor ?? tokens.primaryDark
    )
    const [cornerRadius, setCornerRadius] = useState<CornerRadius>(
        initial?.cornerRadius ? (CORNER_RADIUS_FROM_API[initial.cornerRadius] ?? '0px') : '0px'
    )
    const [typography, setTypography] = useState(
        initial?.typography ? (TYPOGRAPHY_FROM_API[initial.typography] ?? 'modern') : 'modern'
    )
    const [cardStyle, setCardStyle] = useState<CardStyle>(
        initial?.cardStyle ? (CARD_STYLE_FROM_API[initial.cardStyle] ?? 'gradient') : 'gradient'
    )
    const [slides, setSlides] = useState<OnboardingSlide[]>([])
    const [showSlideModal, setShowSlideModal] = useState(false)
    const [slideForm, setSlideForm] = useState<NewSlideForm>(EMPTY_SLIDE_FORM)
    const [showSavedToast, setShowSavedToast] = useState(false)
    const [saving, setSaving] = useState(false)

    const activeVariant = useMemo(
        () => VARIANTS.find((v) => v.id === variant) ?? VARIANTS[0],
        [variant],
    )

    useEffect(() => {
        (async () => {
            const res = await getOnboardingSlides()
            const loaded: OnboardingSlide[] = (res?.slides ?? [])
                .filter((s: any) => s.sliderTitle && s.mobileSliderImage && s.webSliderImage)
                .map((s: any) => ({
                    id: String(s.id),
                    title: s.sliderTitle,
                    description: s.sliderBody ?? '',
                    mobileImageUrl: s.mobileSliderImage,
                    webImageUrl: s.webSliderImage,
                }))
            setSlides(loaded)
        })()
    }, [])

    useEffect(() => {
        try {
            const colors: StoredThemeColors = { primaryColor, secondaryColor, darkSecondaryColor }
            window.localStorage.setItem(THEME_COLORS_STORAGE_KEY, JSON.stringify(colors))
        } catch {
            // localStorage unavailable (e.g. private browsing) — colours just won't persist
        }
    }, [primaryColor, secondaryColor, darkSecondaryColor])

    const slideCountValid = slides.length >= MIN_SLIDES && slides.length <= MAX_SLIDES

    const handleNext = async () => {
        if (step === BRANDING_STEPS.length - 1 && !slideCountValid) return
        // `saving` already gates the button (disabled={saving || ...}), so a
        // single bound onClick + this flag together prevent re-entrant submits.
        if (saving) return

        setSaving(true)
        try {
            if (step === 0) {
                const payload: SelectThemeVariant = { themeVariant: VARIANT_API_MAP[variant] }
                const ok = await selectThemeVariant(payload)
                if (!ok) return
            } else if (step === 1) {
                // NOTE: ThemeConfigurationRequest in params.ts still declares
                // primaryColor/secondaryColor/accentColor/fontFamily, but this
                // page has always sent primaryBrandColour/secondaryBrandColour/
                // tertiaryBrandColour/cornerRadius/typography/cardStyle. The
                // index signature lets this compile either way — the interface
                // itself should be corrected to the real field names so it
                // stops being misleading.
                const payload: ThemeConfigurationRequest = {
                    primaryBrandColour: primaryColor,
                    secondaryBrandColour: secondaryColor,
                    tertiaryBrandColour: darkSecondaryColor,
                    cornerRadius: CORNER_RADIUS_API_MAP[cornerRadius],
                    typography: TYPOGRAPHY_API_MAP[typography] ?? typography,
                    cardStyle: CARD_STYLE_API_MAP[cardStyle],
                }
                const ok = await updateThemeConfiguration(payload)
                if (!ok) return
            } else if (step === BRANDING_STEPS.length - 1) {
                const payload: OnboardingSlideRequest[] = slides.map((slide) => ({
                    sliderTitle: slide.title,
                    sliderBody: slide.description,
                    mobileSliderImages: slide.mobileFile as File,
                    webSliderImages: slide.webFile as File,
                }))
                const ok = await updateOnboardingSlides(payload)
                if (!ok) return
            }

            // Notify the parent that this step's request came back OK
            // (200 or 204) before moving the wizard forward.
            onStepComplete?.(BRANDING_STEP_KEYS[step])

            if (step < BRANDING_STEPS.length - 1) {
                setStep((s) => s + 1)
            } else {
                onComplete?.({
                    variant, primaryColor, secondaryColor, darkSecondaryColor,
                    cornerRadius, typography, cardStyle, logo: null, slides,
                })
            }
        } finally {
            setSaving(false)
        }
    }

    const handleTab = (i: number) => {
        if (i <= step) setStep(i)
    }
    const openSlideModal = () => {
        setSlideForm(EMPTY_SLIDE_FORM)
        setShowSlideModal(true)
    }
    const canSaveSlide = !!(
        slideForm.title.trim() &&
        slideForm.description.trim() &&
        slideForm.mobileFile &&
        slideForm.webFile
    )
    const saveSlide = () => {
        if (!canSaveSlide || slides.length >= MAX_SLIDES) return
        setSlides((s) => [...s, {
            id: `${Date.now()}`,
            title: slideForm.title,
            description: slideForm.description,
            mobileImage: slideForm.mobileImage,
            webImage: slideForm.webImage,
            mobileImageUrl: slideForm.mobileImageUrl,
            webImageUrl: slideForm.webImageUrl,
            mobileFile: slideForm.mobileFile,
            webFile: slideForm.webFile,
        }])
        setShowSlideModal(false)
        setShowSavedToast(true)
    }
    const deleteSlide = async (id: string) => {
        const ok = await deleteOnboardingSlide(Number(id))
        if (!ok) return
        setSlides((s) => s.filter((slide) => slide.id !== id))
    }
    return (
        <Box className={`institution-branding ${className}`} {...rest}>
            <div className="institution-branding__stepper">
                {BRANDING_STEPS.map((label, i) => (
                    <button
                        type="button"
                        key={label}
                        className={`institution-branding__tab ${i === step ? 'is-active' : ''} ${i < step ? 'is-done' : ''}`}
                        onClick={() => handleTab(i)}
                    >
                        <span className="institution-branding__tab-line" />
                        <span className="institution-branding__tab-label" style={fontRegular}>{label}</span>
                    </button>
                ))}
            </div>
            <Card topMargin="1.6" padding="3.2" radius="1.2">
                <Flex justify="space-between" items="flex-start" wrap="wrap" gap="1.6">
                    {step === 0 && (
                        <Text fontSize="1.8" style={fontMedium}>
                            Select Mobile & Internet Banking Design
                        </Text>
                    )}
                    {step === 1 && (
                        <Text fontSize="1.5" style={fontMedium}>
                            Configure Theme
                        </Text>
                    )}
                    {step === 2 && (
                        <Text fontSize="1.8" style={fontMedium}>
                            Onboarding Slide
                        </Text>
                    )}
                    <Button
                        variant="primary"
                        size="md"
                        style={fontRegular}
                        onClick={handleNext}
                        disabled={saving || (step === BRANDING_STEPS.length - 1 && !slideCountValid)}
                    >
                        {saving ? 'Saving...' : step === BRANDING_STEPS.length - 1 ? 'Save & Finish' : 'Save & Next'}
                    </Button>
                </Flex>
                {step === 0 && (
                    <Box topMargin="2.4" className="institution-branding__variants">
                        {VARIANTS.map((v) => (
                            <PreviewOption
                                key={v.id}
                                checked={variant === v.id}
                                onChange={() => setVariant(v.id)}
                            >
                                <div className={`institution-branding__mockup institution-branding__mockup--${v.id}`}>
                                    <img src={v.web} alt="" className="institution-branding__web" />
                                    <img src={v.mobile} alt="" className="institution-branding__mobile" />
                                </div>
                            </PreviewOption>
                        ))}
                    </Box>
                )}
                {step === 1 && (
                    <Flex topMargin="2.4" gap="3.2" items="flex-start" wrap="wrap" className="institution-branding__configure">
                        <Box className="institution-branding__form">
                            <Text fontSize="1.5" style={{ ...fontMedium, color: '#344054' }}>Brand Colors</Text>
                            <Flex topMargin="0.8" gap="1.6" wrap="wrap">
                                <ColorField label="Primary Colour" value={primaryColor} onChange={setPrimaryColor} />
                                <ColorField label="Secondary Colour" value={secondaryColor} onChange={setSecondaryColor} />
                            </Flex>
                            <Text topMargin="1.6" fontSize="1.5" style={{ ...fontMedium, color: '#344054' }}>Other Colours</Text>
                            <Box topMargin="0.8" maxWidth="24">
                                <ColorField label="Dark Secondary Colour" value={darkSecondaryColor} onChange={setDarkSecondaryColor} />
                            </Box>
                            <Box topMargin="0.8">
                                <InfoBanner variant="info">
                                    This colour is applied to the dark<br />
                                    onboarding panel, sidebar, and header
                                </InfoBanner>
                            </Box>
                            <Text topMargin="1.6" fontSize="1.5" style={{ ...fontMedium, color: '#344054' }}>Corner Radius</Text>
                            <Box topMargin="0.8" className="institution-branding__corner-options">
                                {CORNER_OPTIONS.map((opt) => (
                                    <label
                                        key={opt.value}
                                        className={`corner-option ${cornerRadius === opt.value ? 'is-active' : ''}`}
                                    >
                                        <input
                                            type="radio"
                                            name="corner-radius"
                                            checked={cornerRadius === opt.value}
                                            onChange={() => setCornerRadius(opt.value)}
                                        />
                                        <span className="corner-option__bar" style={{ borderRadius: opt.value }} />
                                        <span className="corner-option__row">
                                            <span className="corner-option__dot" />
                                            <span className="corner-option__label">{opt.label}</span>
                                        </span>
                                    </label>
                                ))}
                            </Box>
                            <Text topMargin="1.6" fontSize="1.5" style={{ ...fontMedium, color: '#344054' }}>Typography</Text>
                            <Box topMargin="0.8" className="institution-branding__typography-field">
                                <SelectField
                                    value={typography}
                                    onChange={setTypography}
                                    options={TYPOGRAPHY_OPTIONS.map(({ label, value }) => ({ label, value }))}
                                />
                            </Box>
                            <Text topMargin="1.6" fontSize="1.5" style={{ ...fontMedium, color: '#344054' }}>Dashboard Card Style</Text>
                            <Box topMargin="0.8" className="institution-branding__card-styles">
                                {CARD_STYLE_OPTIONS.map((opt) => (
                                    <RadioCard
                                        key={opt.value}
                                        name="card-style"
                                        checked={cardStyle === opt.value}
                                        onChange={() => setCardStyle(opt.value)}
                                        label={(
                                            <span className="stacked-label">
                                                <strong>{opt.title}</strong>
                                                <small>{opt.help}</small>
                                            </span>
                                        )}
                                    />
                                ))}
                            </Box>
                        </Box>
                        <Box className="institution-branding__preview-col">
                            <LivePreview
                                variant={activeVariant}
                                primaryColor={primaryColor}
                                secondaryColor={secondaryColor}
                                darkSecondaryColor={darkSecondaryColor}
                            />
                        </Box>
                    </Flex>
                )}
                {step === 2 && (
                    <Flex topMargin="2.4" gap="3.2" items="flex-start" wrap="wrap" className="institution-branding__configure">
                        <Box className="institution-branding__form">
                            <Button
                                variant="primary"
                                size="md"
                                block
                                style={fontRegular}
                                icon={<img src={plusIcon} alt="" style={{ width: '1.6rem', height: '1.6rem' }} />}
                                onClick={openSlideModal}
                                disabled={slides.length >= MAX_SLIDES}
                            >
                                Add New Slide
                            </Button>
                            <Box topMargin="1.2">
                                <InfoBanner variant="info">
                                    Kindly add a minimum of 3 and a maximum of 5 sliders to continue to the next step
                                </InfoBanner>
                            </Box>
                            <Box topMargin="1.6" className="institution-branding__slides">
                                {slides.map((slide) => (
                                    <Card
                                        key={slide.id}
                                        className="institution-branding__slide"
                                        padding="1.6"
                                        topMargin="0"
                                    >
                                        <Flex gap="1.2" items="flex-start">
                                            <img
                                                src={slide.mobileImageUrl ?? activeVariant.mobile}
                                                alt=""
                                                className="institution-branding__slide-thumb"
                                            />
                                            <Box style={{ flex: 1, minWidth: 0 }}>
                                                <Text
                                                    fontSize="1.4"
                                                    style={fontMedium}
                                                    className="institution-branding__slide-title"
                                                >
                                                    {slide.title}
                                                </Text>
                                                <Text
                                                    fontSize="1.3"
                                                    color={tokens.textMuted}
                                                    style={fontRegular}
                                                    topMargin="0.4"
                                                    className="institution-branding__slide-desc"
                                                >
                                                    {slide.description}
                                                </Text>
                                            </Box>
                                            <Icon size="1.8" onClick={() => deleteSlide(slide.id)}>
                                                <RiDeleteBinLine size={18} color="#B42318" />
                                            </Icon>
                                        </Flex>
                                    </Card>
                                ))}
                            </Box>
                        </Box>
                        <Box className="institution-branding__preview-col">
                            <LivePreview
                                variant={activeVariant}
                                primaryColor={primaryColor}
                                secondaryColor={secondaryColor}
                                darkSecondaryColor={darkSecondaryColor}
                                slideMobileImageUrl={slides[slides.length - 1]?.mobileImageUrl}
                                slideWebImageUrl={slides[slides.length - 1]?.webImageUrl}
                            />
                        </Box>
                    </Flex>
                )}
            </Card>
            <Modal open={showSlideModal} onClose={() => setShowSlideModal(false)} title="New Slider" maxWidth="52rem">
                <Text fontSize="1.4" color={tokens.textMuted} style={fontRegular}>
                    Add images or banners to guide users through their first experience.
                </Text>
                <Box topMargin="2">
                    <TextField
                        label="Slider Title"
                        placeholder="Enter Slider Title"
                        value={slideForm.title}
                        onChange={(title) => setSlideForm((f) => ({ ...f, title }))}
                    />
                </Box>
                <Box topMargin="1.6">
                    <label className="field__label">Body (Max 100 chars)</label>
                    <textarea
                        className="institution-branding__textarea"
                        placeholder="Enter Slider Description"
                        maxLength={100}
                        value={slideForm.description}
                        onChange={(e) => setSlideForm((f) => ({ ...f, description: e.target.value }))}
                    />
                </Box>
                <Box topMargin="1.6">
                    <InfoBanner variant="info">
                        These images should have a transparent background to fit into the design
                    </InfoBanner>
                </Box>
                <Flex topMargin="1.6" gap="1.6" wrap="wrap">
                    <Box style={{ flex: 1, minWidth: '20rem' }}>
                        <FileUpload
                            value={slideForm.mobileImage}
                            label="Upload mobile slider image"
                            hint="(840px X 390px) Preferred — SVG, JPG, JPEG or PNG"
                            accept=".svg,.jpg,.jpeg,.png"
                            height="14"
                            onSelect={(f) => setSlideForm((s) => ({
                                ...s,
                                mobileImage: { name: f.name, size: `${Math.round(f.size / 1024)} KB` },
                                mobileImageUrl: URL.createObjectURL(f),
                                mobileFile: f,
                            }))}
                            onRemove={() => setSlideForm((s) => ({ ...s, mobileImage: null, mobileImageUrl: undefined, mobileFile: undefined }))}
                        />
                    </Box>
                    <Box style={{ flex: 1, minWidth: '20rem' }}>
                        <FileUpload
                            value={slideForm.webImage}
                            label="Web slider image"
                            hint="(1440px X 900px) Preferred — SVG, JPG, JPEG or PNG"
                            accept=".svg,.jpg,.jpeg,.png"
                            height="14"
                            onSelect={(f) => setSlideForm((s) => ({
                                ...s,
                                webImage: { name: f.name, size: `${Math.round(f.size / 1024)} KB` },
                                webImageUrl: URL.createObjectURL(f),
                                webFile: f,
                            }))}
                            onRemove={() => setSlideForm((s) => ({ ...s, webImage: null, webImageUrl: undefined, webFile: undefined }))}
                        />
                    </Box>
                </Flex>
                <Flex justify="flex-end" topMargin="2.4">
                    <Button
                        variant="primary"
                        size="md"
                        style={fontRegular}
                        onClick={saveSlide}
                        disabled={!canSaveSlide}
                    >
                        Save Slider
                    </Button>
                </Flex>
            </Modal>
            {showSavedToast && (
                <Notification
                    message="Slider Saved Successfully"
                    variant="success"
                    position="top-center"
                    onClose={() => setShowSavedToast(false)}
                />
            )}
        </Box>
    )
}
export default InstitutionBranding